/**
 * The reference user's access, per system (21 Oct, Rusty): "see what that
 * user had and add to the new user, or take away, or just review".
 *
 * - `ReferenceEntries` — one card's entries, read-only: label, detail, how
 *   they have it (granted, requested, owner, live) and where it was read,
 *   with the request number and date. The IT Admin glance and the review.
 * - `ReferenceItemPanel` — one checklist item's entries to keep or remove
 *   (a tick per entry), folders and sites to add from Approvers' list, and
 *   the difference with the reference user. The wizard and Backoffice's
 *   step form; the role picker beside it adds role-list entries.
 * - `ReferenceDiffView` — kept / removed / added, as lists.
 * - `ReferenceSelectionView` — what the requester chose, as the note on the
 *   New user request records it ("Roles to select").
 * - `SourceLines` — a source of a card that could not be read (28 Oct: not "not connected").
 */

import { Badge, Button, Checkbox, Chip, Field, Input, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type {
  ReferenceAccessItem,
  ReferenceAccessSystem,
  ReferenceSelectionLine,
  ReferenceSourceState,
} from "@/lib/api";

import type { RoleSelection } from "../lib/access";
import {
  entriesOf,
  hasDiff,
  isKept,
  matchingOptions,
  type ReferenceChoice,
  type ReferenceDiff,
  setAdded,
  setKept,
} from "../lib/reference";

const RELATION_TONE = {
  granted: "success",
  requested: "warning",
  owns: "info",
  assigned: "accent",
} as const;

/** "ITSM request 10390 · 3 Oct 2026", "Approvers list · 21 Oct 2026". */
function SourceText({ item, linkTickets }: { item: ReferenceAccessItem; linkTickets: boolean }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const date = item.as_of ? ` · ${formatDate(item.as_of)}` : "";
  if (item.source === "itsm" && item.source_ref) {
    const words = t("access.reference.source.itsm", { ref: item.source_ref });
    return (
      <Typography as="span" variant="caption" tone="muted">
        {linkTickets && item.ticket_id ? (
          <Link
            to={`/tickets/${encodeURIComponent(item.ticket_id)}`}
            className="underline underline-offset-2"
          >
            {words}
          </Link>
        ) : (
          words
        )}
        {date}
      </Typography>
    );
  }
  return (
    <Typography as="span" variant="caption" tone="muted">
      {t(`access.reference.source.${item.source}` as never)}
      {date}
    </Typography>
  );
}

/** One entry: its words, detail, how they have it and where it was read. */
function EntryText({
  item,
  linkTickets,
  compareName,
}: {
  item: ReferenceAccessItem;
  linkTickets: boolean;
  compareName?: string | null;
}) {
  const { t } = useT("tickets");
  const relation = item.relation ?? "granted";
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {item.detail ? (
          <Typography as="span" variant="body-sm" tone="muted" className="break-words">
            {item.detail}
          </Typography>
        ) : null}
        {relation !== "granted" ? (
          <Badge tone={RELATION_TONE[relation]} variant="subtle" size="xs">
            {t(`access.reference.relation.${relation}`)}
          </Badge>
        ) : null}
        {item.compare_has && compareName ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {t("access.reference.alreadyHas", { name: compareName })}
          </Badge>
        ) : null}
      </span>
      <SourceText item={item} linkTickets={linkTickets} />
    </span>
  );
}

/** An entry's label: its own, or "Access, no roles named" for a generic one. */
export function useEntryLabel() {
  const { t } = useT("tickets");
  return (item: { label: string }) =>
    item.label === "" ? t("access.reference.generic") : item.label;
}

/** One card's entries, read-only (the glance, the review). */
export function ReferenceEntries({
  items,
  linkTickets = false,
}: {
  items: readonly ReferenceAccessItem[];
  linkTickets?: boolean;
}) {
  const label = useEntryLabel();
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {items.map((item) => (
        <li key={item.id} className="flex min-w-0 flex-col gap-0.5">
          <Typography as="span" variant="body-sm" className="font-medium break-words">
            {label(item)}
          </Typography>
          <EntryText item={item} linkTickets={linkTickets} />
        </li>
      ))}
    </ul>
  );
}

/**
 * A source that could not be read just now, in a line. 28 Oct (Rusty): a
 * source that is not connected yet (live 365FO roles, Entra, CRM) says
 * nothing; only the connected ones show.
 */
export function SourceLines({ sources }: { sources: readonly ReferenceSourceState[] }) {
  const { t } = useT("tickets");
  const missing = sources.filter((source) => source.state === "error");
  if (missing.length === 0) {
    return null;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      {missing.map((source) => (
        <li key={source.source}>
          <Typography as="span" variant="caption" tone="muted">
            {t("access.reference.sourceError", {
              source: t(`access.reference.source.${source.source}Name` as never),
            })}
          </Typography>
        </li>
      ))}
    </ul>
  );
}

/** Kept / removed / added as three short lists (nothing when nothing changed). */
export function ReferenceDiffView({ diff }: { diff: ReferenceDiff }) {
  const { t } = useT("tickets");
  if (!hasDiff(diff)) {
    return null;
  }
  const rows = [
    { key: "kept", list: diff.kept, tone: "success" },
    { key: "removed", list: diff.removed, tone: "danger" },
    { key: "added", list: diff.added, tone: "accent" },
  ] as const;
  return (
    <dl
      className="m-0 grid gap-x-3 gap-y-1 sm:grid-cols-[minmax(0,8rem)_1fr]"
      aria-label={t("access.reference.diff.title")}
    >
      {rows
        .filter((row) => row.list.length > 0)
        .map((row) => (
          <div key={row.key} className="contents">
            <dt>
              <Badge tone={row.tone} variant="subtle" size="xs">
                {`${t(`access.reference.diff.${row.key}`)} ${row.list.length}`}
              </Badge>
            </dt>
            <dd className="m-0 text-sm break-words">{row.list.join(", ")}</dd>
          </div>
        ))}
    </dl>
  );
}

/** What the requester chose (the note), for "Roles to select" and the step's form. */
export function ReferenceSelectionView({
  lines,
  labelOf,
}: {
  lines: readonly ReferenceSelectionLine[];
  labelOf: (item: string) => string;
}) {
  const { t } = useT("tickets");
  if (lines.length === 0) {
    return (
      <Typography as="p" variant="caption" tone="muted">
        {t("access.reference.selection.none")}
      </Typography>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {lines.map((line) => (
        <li key={line.item} className="flex min-w-0 flex-col gap-1">
          <Typography as="span" variant="body-sm" className="font-medium">
            {labelOf(line.item)}
          </Typography>
          <ReferenceDiffView
            diff={{ kept: line.kept ?? [], removed: line.removed ?? [], added: line.added ?? [] }}
          />
        </li>
      ))}
    </ul>
  );
}

export interface ReferenceItemPanelProps {
  /** The reference user's name, as the heading says it. */
  personName: string;
  /** The cards of this checklist item (Folders and SharePoint for "shared_folders"). */
  systems: readonly ReferenceAccessSystem[];
  selection: RoleSelection;
  choice: ReferenceChoice;
  onChange: (next: { selection: RoleSelection; choice: ReferenceChoice }) => void;
  /** The difference so far, computed by the caller (it knows the role list). */
  diff: ReferenceDiff;
  /** A Change: the person it is for, whose entries are marked "has it already". */
  compareName?: string | null;
  /** The role picker beside the panel adds role-list entries: say so. */
  pickerBelow?: boolean;
  /** A unique prefix for field names (one panel per item on a page). */
  name: string;
  /** Shown before anything is copied: "Copy into this request" (the step's form). */
  onApply?: () => void;
}

/**
 * One checklist item's entries to keep or remove, and folders or sites to
 * add. Before "Copy into this request" it is a list to review; after, each
 * entry has a tick (granted and live entries start ticked).
 */
export function ReferenceItemPanel({
  personName,
  systems,
  selection,
  choice,
  onChange,
  diff,
  compareName = null,
  pickerBelow = false,
  name,
  onApply,
}: ReferenceItemPanelProps) {
  const { t } = useT("tickets");
  const label = useEntryLabel();
  const entries = entriesOf(systems) as ReferenceAccessItem[];
  const options = systems.flatMap((system) => system.add_options ?? []);
  return (
    <section
      aria-label={t("access.reference.itemTitle", { name: personName })}
      className="border-border-subtle bg-bg-subtle flex flex-col gap-3 rounded-md border p-3"
    >
      <Typography as="h3" variant="body-sm" className="font-medium">
        {t("access.reference.itemTitle", { name: personName })}
      </Typography>
      {entries.length === 0 ? (
        <Typography as="p" variant="caption" tone="muted">
          {t("access.reference.nothing", { name: personName })}
        </Typography>
      ) : choice.applied ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {entries.map((item) => (
            <li key={item.id} className="flex min-w-0 flex-col gap-0.5">
              <Checkbox.Root
                checked={isKept(item, selection, choice)}
                onCheckedChange={(next) =>
                  onChange(setKept(item, next === true, selection, choice))
                }
                label={label(item)}
              />
              <span className="ps-7">
                <EntryText item={item} linkTickets={false} compareName={compareName} />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <ReferenceEntries items={entries} />
          {onApply ? (
            <Button size="sm" variant="outline" className="self-start" onClick={onApply}>
              {t("access.reference.copyAll")}
            </Button>
          ) : null}
        </>
      )}
      <SourceLines
        sources={[
          ...new Map(
            systems.flatMap((system) => system.sources ?? []).map((src) => [src.source, src]),
          ).values(),
        ]}
      />
      {choice.applied && options.length > 0 ? (
        <AddFromList
          name={name}
          options={options}
          choice={choice}
          onChoice={(next) => onChange({ selection, choice: next })}
          entryIds={entries.map((e) => e.id)}
        />
      ) : null}
      {choice.applied && pickerBelow ? (
        <Typography as="p" variant="caption" tone="muted">
          {t("access.reference.addHint")}
        </Typography>
      ) : null}
      {choice.applied ? (
        hasDiff(diff) ? (
          <ReferenceDiffView diff={diff} />
        ) : (
          <Typography as="p" variant="caption" tone="muted">
            {t("access.reference.diff.none")}
          </Typography>
        )
      ) : null}
    </section>
  );
}

/** Folders and sites from Approvers → Resource owners: search, add, take back. */
function AddFromList({
  name,
  options,
  choice,
  onChoice,
  entryIds,
}: {
  name: string;
  options: readonly { id: string; label: string; detail?: string | null }[];
  choice: ReferenceChoice;
  onChoice: (choice: ReferenceChoice) => void;
  entryIds: readonly string[];
}) {
  const { t } = useT("tickets");
  const [query, setQuery] = useState("");
  const found = matchingOptions(options, query, [...choice.added, ...entryIds]);
  const added = choice.added.flatMap((id) => options.filter((o) => o.id === id));
  return (
    <div className="flex flex-col gap-2">
      <Field
        name={`${name}-add`}
        label={t("access.reference.addFolder")}
        hint={t("access.reference.addFolderHint")}
      >
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
        />
      </Field>
      {query.trim() !== "" ? (
        found.length === 0 ? (
          <Typography as="p" variant="caption" tone="muted" aria-live="polite">
            {t("access.reference.addNone")}
          </Typography>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0" aria-live="polite">
            {found.map((option) => (
              <li key={option.id} className="flex min-w-0 flex-wrap items-center gap-2">
                <Typography as="span" variant="body-sm" className="min-w-0 break-words">
                  {option.label}
                </Typography>
                {option.detail ? (
                  <Typography as="span" variant="caption" tone="muted">
                    {t("access.reference.owner", { name: option.detail })}
                  </Typography>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  tone="neutral"
                  className="ms-auto"
                  aria-label={t("access.reference.addNamed", { item: option.label })}
                  onClick={() => onChoice(setAdded(choice, option.id, true))}
                >
                  {t("access.reference.addButton")}
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}
      {added.length > 0 ? (
        <div className="flex flex-wrap gap-2" aria-label={t("access.reference.diff.added")}>
          {added.map((option) => (
            <Chip
              key={option.id}
              size="sm"
              onRemove={() => onChoice(setAdded(choice, option.id, false))}
              removeLabel={t("access.reference.removeAdded", { item: option.label })}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  );
}
